import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface TrackStatus {
  id: string;
  name: string;
  type: "pending" | "completed" | "rejected";
  followUpEnabled?: boolean;
}

export interface TrackConfig {
  id: string;
  name: string;
  description: string;
  assignedStageIds: number[];
  isMandatory: boolean;
  mandatoryInStageIds: number[];
  enforcementType: "soft" | "hard";
  followUpEnabled?: boolean;
  followUpDays?: number | null;
  autoAssignUser?: boolean;
  /** Lead based (default) or Deal based. */
  grain?: TrackGrain;
  statuses: TrackStatus[];
}

/** What a track is kept on. Missing (older server) = "lead". */
export type TrackGrain = "lead" | "deal";

export interface TracksManagementState {
  tracks: TrackConfig[];
  selectedTrackId: string | null;
  isAddTrackOpen: boolean;
  isEditTrackOpen: boolean;
  isDeleteTrackOpen: boolean;
}

/** API track shape (from getAllPipeline / tracks pipeline) */
export interface ApiTrack {
  id: number;
  name: string;
  description: string | null;
  enforcementType: "soft" | "hard" | "none";
  followUpEnabled?: boolean;
  followUpDays?: number | null;
  autoAssignUser?: boolean;
  grain?: TrackGrain;
  subTracks?: {
    id: number;
    name: string;
    isDefault: boolean;
    isMandatory: boolean;
    isAutoComplete: boolean;
    statusType: "pending" | "active" | "rejected";
    followUpEnabled?: boolean;
  }[];
  stageTracks?: {
    stageId: number;
    isMandatoryInStage: boolean;
    stage?: { id: number; name: string };
  }[];
}

export function mapApiTrackToConfig(api: ApiTrack): TrackConfig {
  const stageTracks = api.stageTracks ?? [];
  const subTracks = api.subTracks ?? [];
  const enforcementType =
    api.enforcementType === "none" ? "soft" : api.enforcementType;
  const isMandatory = api.enforcementType !== "none";

  return {
    id: String(api.id),
    name: api.name,
    description: api.description ?? "",
    assignedStageIds: stageTracks.map((st) => st.stageId),
    isMandatory,
    mandatoryInStageIds: stageTracks
      .filter((st) => st.isMandatoryInStage)
      .map((st) => st.stageId),
    enforcementType,
    followUpEnabled: api.followUpEnabled ?? false,
    followUpDays: api.followUpDays ?? null,
    autoAssignUser: api.autoAssignUser ?? false,
    grain: api.grain ?? "lead",
    statuses: subTracks.map((st) => ({
      id: String(st.id),
      name: st.name,
      followUpEnabled: st.followUpEnabled ?? true,
      type: (st.statusType === "active"
        ? "completed"
        : st.statusType) as TrackStatus["type"],
    })),
  };
}

const initialState: TracksManagementState = {
  tracks: [],
  selectedTrackId: null,
  isAddTrackOpen: false,
  isEditTrackOpen: false,
  isDeleteTrackOpen: false,
};

const tracksManagementSlice = createSlice({
  name: "tracksManagement",
  initialState,
  reducers: {
    setSelectedTrack(state, action: PayloadAction<string | null>) {
      state.selectedTrackId = action.payload;
    },

    setTracks(state, action: PayloadAction<TrackConfig[]>) {
      state.tracks = action.payload;
      if (state.tracks.length > 0 && !state.selectedTrackId) {
        state.selectedTrackId = state.tracks[0].id;
      }
      if (
        state.tracks.length > 0 &&
        state.selectedTrackId &&
        !state.tracks.some((t) => t.id === state.selectedTrackId)
      ) {
        state.selectedTrackId = state.tracks[0].id;
      }
      if (state.tracks.length === 0) {
        state.selectedTrackId = null;
      }
    },

    setAddTrackOpen(state, action: PayloadAction<boolean>) {
      state.isAddTrackOpen = action.payload;
    },

    setEditTrackOpen(state, action: PayloadAction<boolean>) {
      state.isEditTrackOpen = action.payload;
    },

    setDeleteTrackOpen(state, action: PayloadAction<boolean>) {
      state.isDeleteTrackOpen = action.payload;
    },

    addTrack(state, action: PayloadAction<Omit<TrackConfig, "id">>) {
      const id = `track-${Date.now()}`;
      state.tracks.push({ ...action.payload, id });
      state.selectedTrackId = id;
      state.isAddTrackOpen = false;
    },

    updateTrack(
      state,
      action: PayloadAction<{ id: string; updates: Partial<TrackConfig> }>
    ) {
      const idx = state.tracks.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) {
        state.tracks[idx] = { ...state.tracks[idx], ...action.payload.updates };
      }
    },

    deleteTrack(state, action: PayloadAction<string>) {
      state.tracks = state.tracks.filter((t) => t.id !== action.payload);
      if (state.selectedTrackId === action.payload) {
        state.selectedTrackId = state.tracks[0]?.id ?? null;
      }
      state.isDeleteTrackOpen = false;
    },

    addTrackStatus(
      state,
      action: PayloadAction<{ trackId: string; status: TrackStatus }>
    ) {
      const track = state.tracks.find((t) => t.id === action.payload.trackId);
      if (track) {
        track.statuses.push(action.payload.status);
      }
    },

    updateTrackStatus(
      state,
      action: PayloadAction<{
        trackId: string;
        statusId: string;
        updates: Partial<TrackStatus>;
      }>
    ) {
      const track = state.tracks.find((t) => t.id === action.payload.trackId);
      if (track) {
        const status = track.statuses.find(
          (s) => s.id === action.payload.statusId
        );
        if (status) {
          Object.assign(status, action.payload.updates);
        }
      }
    },

    removeTrackStatus(
      state,
      action: PayloadAction<{ trackId: string; statusId: string }>
    ) {
      const track = state.tracks.find((t) => t.id === action.payload.trackId);
      if (track && track.statuses.length > 1) {
        track.statuses = track.statuses.filter(
          (s) => s.id !== action.payload.statusId
        );
      }
    },

    resetTracksManagement() {
      return initialState;
    },
  },
});

export const {
  setSelectedTrack,
  setTracks,
  setAddTrackOpen,
  setEditTrackOpen,
  setDeleteTrackOpen,
  addTrack,
  updateTrack,
  deleteTrack,
  addTrackStatus,
  updateTrackStatus,
  removeTrackStatus,
  resetTracksManagement,
} = tracksManagementSlice.actions;

export default tracksManagementSlice.reducer;
